import * as api from "../clients/slashid-api"
import * as domain from "../domain/index"

import { type SlashID, SlashID as SlashIDConstructor } from "."
import { SlashIDOptions } from "../domain/options"
import { BaseUser } from "./user.base"

export class BrowserUser extends BaseUser {
    private _slashid: SlashID

    /**
     * Reconstruct an authenticated user from its token value. An instance can be created in multiple ways:
     * - by calling {@link SlashID.id} to perform authentication
     * - by calling {@link SlashID.getUser} to reconstruct a user from a token while reusing the existing SDK instance
     * - by creating it directly using the constructor (not advised)
     *
     * Instances can only be constructed in a browser environment due to dependency on {@link SlashID}.
     *
     * @param token A user {@link token} value.
     * @param optionsOrSid SlashID instance or SlashID connection options. It is preferred to pass in the SlashID instance
     *
     * @throws `TypeError` If the given `optionsOrSid`, if defined, fail validation, or
     * the given token cannot be decoded.
     */
    constructor(token: domain.UserTokenText | domain.TokenContainerText, optionsOrSid?: SlashID)
    /**
     * @deprecated use SlashId instead of SlashIDOptions as it ensures the expected behaviour of the internal event emitter
     */
    constructor(token: domain.UserTokenText | domain.TokenContainerText, optionsOrSid?: SlashIDOptions)
    constructor(token: domain.UserTokenText | domain.TokenContainerText, optionsOrSid?: SlashIDOptions | SlashID) {
        // both the options and SlashID instances have the same fields (baseURL, sdkURL, oid)
        super(token, optionsOrSid)

        this._slashid = optionsOrSid instanceof SlashIDConstructor ? optionsOrSid : new SlashIDConstructor(optionsOrSid)
    }

    /**
     * Request the user to authenticate with the given method. This method is essentially equivalent to
     * {@link SlashID.id}, but since the user is already authenticated you can omit the `handle` or
     * `authenticationFactor` parameters, depending on the desired outcome. Please refer to the examples
     * for the use cases this method covers.
     *
     * @param handle A user handle, either a new one to be associated to the user, or an existing
     * one to deliver the authetication request to.
     *
     * @param authenticationFactor The authentication factor, please to refer to {@link SlashID.id}
     * documentation for details.
     *
     * @throws `slashid.errors.InvalidAuthenticationMethodError` if the chosen method is
     * incompatible with the given handle or is not available on the current device.
     * `Error` if the operation fails otherwise.
     *
     * @example
     *
     * One thing you can do with `mfa()` is attach other handles to the user.
     * For example, the user has already authenticated via e-mail magic link with
     * {@link SlashID.id}, but you want to also attach their phone number. In that case all
     * the method requires is the handle, not
     *
     * ```js
     * await user.mfa({
     *   type: "phone_number",
     *   value: "+13337777777"
     * })
     * ```
     *
     * SlashID will send a challenge SMS to the phone number to verify it. If `mfa()` returns
     * without throwing any error the procedure is complete and the user object will have been
     * updated with a refreshed {@link token}. For future authentications the user will be able
     * to use the newly-attached phone number as an alternative to the e-mail address. Consider
     * though that this call only verified ownership of the phone number and added it to the user
     * handles, as such the {@link authentication} array stays unchanged.
     *
     * If you want to step-up the user authentication, or simply re-authenticate the user,
     * you can do so by also providing an authentication factor. For example let's assume
     * you want to send a magic link to one of the user's e-mail address:
     *
     * ```js
     * await user.mfa({
     *   type: "email_address",
     *   value: "..." // one previously-attached address, or a new one to be attached
     * }, {
     *   method: "email_link"
     * })
     * ```
     *
     * If the e-mail address provided above was not attached to the user, SlashID will verify it
     * and also perform the authentication. Once again, when `mfa()` returns the user will have
     * a refreshed {@link token}, but this time its {@link authentication} array will also include
     * `"email_link"` in addition to the method chosen for the first authentication with
     * {@link SlashID.id}.
     *
     * The only edge case to note is that for performing MFA with WebAuthn authentication
     * there's no need to specify an handle, as the ceremony happens on the current device:
     *
     * ```js
     * await user.mfa(undefined, {
     *   method: "webauthn"
     * })
     * ```
     */
    public async mfa(handleOrFactor: any, authenticationFactor?: domain.Factor): Promise<void> {
        let handle: api.PersonHandle | undefined = undefined

        if (!handleOrFactor) {
            throw new Error("at least one of PersonHandle or Factor is required")
        }
        if (handleOrFactor.type && handleOrFactor.value) {
            handle = handleOrFactor as api.PersonHandle
        }
        if (handleOrFactor.method) {
            authenticationFactor = handleOrFactor as domain.Factor
        }

        // On WebAuthn, if the key does not exist on the authenticator:
        //  - Firefox throws "InvalidStateError: An attempt was made to use an object that is not, or is no longer, usable"
        //  - Chrome throws "NotAllowedError: The operation either timed out or was not allowed. See: https://www.w3.org/TR/webauthn-2/#sctn-privacy-considerations-client."
        const updatedUser = await this._slashid!!._id(this.decoded.oid!!, handle, authenticationFactor, this._token)
        this.updateToken(updatedUser.toString())
        return
    }
}
